import { useEffect, useState, useRef } from 'react';
import {
  CartEventsEnum,
  ApiProjections,
  type Cart,
  cartApi,
  type CartEventDataInterface,
  type CartEventPayloadType,
  CartEvents,
  CartEventsVersionsEnum,
  updateCart,
  updateIsLoading,
  updateBlockerException,
  CartConstantsEnum,
  getMyListsByTypeSliceReq,
  UserListResponse,
  ListType,
  updateMyList,
  myListApi,
  OrderType,
  updateListsByUser,
  ListResponse,
  getOrderTypeConfig,
  isGroupOrder,
  hasInvalidCoupon,
  getActiveCart,
  isRegularOrder,
  tealiumEventPublisher,
  GetCartsResponse,
  updateShippingAddressInLS,
  SubCartDetail,
  ErrorAPI,
} from '@commerce-cart-webcomponents/sdk';
import {
  FAIcon,
  useSession,
  useTranslation,
  useNextgenRouter,
  Theme,
  NextgenImage,
  Modal,
  LoaderType,
} from '@nextgen-web-framework/all';
import { PayloadAction, AnyAction } from '@reduxjs/toolkit';
import { EventService, StorageService, ConsoleLogger, type AppDispatch } from '@nextgen-web-framework/core';
import { useDispatch } from 'react-redux';
import { WebEventsEnum, WebEventsVersionsEnum } from '@commerce-webcomponents/sdk';
import { useAddressUpdate } from '@commerce-webcomponents/ui';
import { ProfileConstantsEnum } from '@commerce-profile-webcomponents/sdk';
import { useCartNotification } from '../../common/hooks/useCartNotification';
import { CartNotificationTheme } from '../../common/components/cart-notification/types/enums';
import { TOAST_TIMEOUT } from '../../common/types/enums';
import { isProxyOrder } from '../../../../source/sdk/utils/cartCommonHelpers';
import { registrationAttributesType } from '../account-registration-banner/AccountRegistrationBanner';
import { useExceptionToast } from '../../common';
import styles from './MiniCart.module.scss';
import { EnableListApiConfig, RouteType } from './types/enums';
interface MiniCartProps {
  displayCount?: number;
  icon?: React.ReactNode;
  /**
   * @deprecated, `envConfig` we no longer need this envConfig prop,
   * framework is automatically attaching the baseURL passed by E2E to API requests.
   */
  envConfig?: { [key: string]: string };
  elementClass?: string;
  isGroupCartCb?: (isGCO: boolean) => void;
}

export const MiniCart = (props: MiniCartProps) => {
  const t = useTranslation();
  const { updateAddress } = useAddressUpdate();
  const { displayCount, icon, elementClass = '', isGroupCartCb } = props;
  const { createToast, deleteToast } = useCartNotification('miniCart');
  const { isAnonymous, isABO, isRegistered, isActor } = useSession();
  const [count, setCount] = useState<number>(displayCount || 0);
  const router = useNextgenRouter();
  const queryCartId = router.params?.cartId ?? '';
  const subscriptionIds = useRef<string[]>([]);
  const orderTypeConfig = getOrderTypeConfig();
  const [orderType, setOrderType] = useState<OrderType>(OrderType.Regular);
  const triggerToast = useExceptionToast();

  const dispatch = useDispatch<AppDispatch>();
  const isAnon = isAnonymous();
  const mergeCartSuccessText = t('cart.miniCart.mergeCartSuccess');
  const elementCustomrClass = styles[elementClass] || elementClass;

  const [showRegSponsorChangePopup, setShowRegSponsorChangePopup] = useState(false);

  const updateMiniCartCount = (res: Cart) => {
    if (Object.keys(res).length > 0) {
      setCount(() => res.totalEntries as number);
      const isGroupCart = res?.orderType === OrderType.Gco;
      isGroupCartCb?.(isGroupCart);
      setOrderType(res?.orderType as OrderType);
    }
  };

  const fireListApi = (pathname: string) => EnableListApiConfig[pathname] ?? true;

  const updateAddressHandler = (currentActiveCart: SubCartDetail) => {
    const amwayUserIdinCheckoutLS = StorageService.getLocalStorage().retrieve('checkoutProfile') as unknown as {
      fulfillmentType: {
        serviceLevel: {
          deliveryAddress: {
            ownerId?: string;
            accountNumber?: string;
          };
        };
      };
    };
    const ownerId = amwayUserIdinCheckoutLS?.fulfillmentType?.serviceLevel?.deliveryAddress?.ownerId;
    const accountNum = amwayUserIdinCheckoutLS?.fulfillmentType?.serviceLevel?.deliveryAddress?.accountNumber;
    //added accountNum check for same profile and multi account scenario
    if (
      !isAnon &&
      (ownerId !== currentActiveCart?.amwayUser?.amwayUserId ||
        String(accountNum) !== currentActiveCart?.amwayUser?.accountId)
    ) {
      updateShippingAddressInLS(
        currentActiveCart?.amwayUser?.amwayUserId as string,
        currentActiveCart?.amwayUser?.accountId as string,
      ).then(updateAddress);
    }
  };

  const getCart = () => {
    dispatch(updateIsLoading(true));
    const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);

    //Only need this for non anonoymous user
    if (!cartId && !isAnon) {
      cartApi
        .getCarts({
          projections: ApiProjections.MIN,
          options: {
            skipCartIdStoreInLS: true,
            skipCartResStoreInLS: true,
            skipIsGroupCartStoreInLS: true,
            skipVersionStoreInLS: true,
          },
        })
        .then(async (res) => {
          const { carts } = res as GetCartsResponse;
          if (carts.length) {
            try {
              const [latestCart] = carts;
              StorageService.getLocalStorage().store(CartConstantsEnum.CartId, latestCart.id);
              StorageService.getLocalStorage().store(CartConstantsEnum.CartReqVersion, latestCart.version);
              StorageService.getLocalStorage().store(CartConstantsEnum.IsGroupCart, isGroupOrder(latestCart.orderType));

              const currentLatestCartWrapper = await cartApi.getSubCart({
                cartId: latestCart.id,
                subCartId: latestCart.id,
                projections: ApiProjections.DETAILED,
                options: {
                  skipCartResStoreInLS: true,
                },
              });

              const currentActiveCart = getActiveCart(currentLatestCartWrapper);

              dispatch(updateCart(currentLatestCartWrapper));
              setCount(() => currentLatestCartWrapper?.totalEntries as number);
              updateAddressHandler(currentActiveCart);
            } finally {
              dispatch(updateIsLoading(false));
            }
          }
        })
        .catch((e) => ConsoleLogger.log('Error occured ', e))
        .finally(() => dispatch(updateIsLoading(false)));
    } else if (!cartId) {
      dispatch(updateIsLoading(false));
    }

    if (cartId) {
      cartApi
        .getSubCart({
          cartId,
          subCartId: cartId,
          projections: ApiProjections.DETAILED,
          options: {
            skipCartResStoreInLS: true,
          },
        })
        .then((res) => {
          dispatch(updateIsLoading(false));
          dispatch(updateBlockerException(false));
          const isGroupCart = (res as Cart)?.orderType === OrderType.Gco;
          isGroupCartCb?.(isGroupCart);
          setOrderType((res as Cart)?.orderType as OrderType);
          setCount(() => (res as Cart).totalEntries as number);
          dispatch(updateCart(res as Cart));
          const mainSubCart = getActiveCart(res, queryCartId as string);
          const couponsError = hasInvalidCoupon(res);
          if (router.pathname?.includes(RouteType.CART)) {
            tealiumEventPublisher(res, CartEventsEnum.ViewCart);
          }

          if (router.pathname?.includes(RouteType.CART) && couponsError) {
            cartApi
              .applyCoupon({
                cartId: res.id,
                subCartId: mainSubCart.subCartId as string,
                payload: {
                  coupons: mainSubCart.couponResults?.appliedCoupons || [],
                  version: res.version,
                },
              })
              .then((cart) => {
                dispatch(updateCart(cart as Cart));
                CartEvents.publishEvent({
                  event: CartEventsEnum.UpdateCartEntryQty,
                  payload: {
                    cart,
                    cartId: (cart as Cart).id,
                  },
                  sync: true,
                });
                createToast(
                  {
                    theme: CartNotificationTheme.YELLOW,
                    rightSeperator: false,
                    clickIcon: 'times',
                    icon: 'exclamation-triangle',
                    content: couponsError.detail || '',
                    timeOut: TOAST_TIMEOUT,
                  },
                  deleteToast,
                );
              });
          }
        })
        .catch(() => {
          dispatch(updateIsLoading(false));
          dispatch(updateBlockerException(true));
        });
    }
  };

  const clearCart = (msg: string, data: CartEventDataInterface | CartEventPayloadType) => {
    cartApi.clearCartEntries({ cartId: data.cartId }).then((res) => {
      setCount((res as Cart).totalEntries as number);
      if (!queryCartId) {
        dispatch(updateCart(res as Cart));
      }
      StorageService.getLocalStorage().remove('cartId');
    });
  };

  const mergeCart = () => {
    cartApi
      .mergeCart()
      .then((res) => {
        const currentCart = isGroupOrder(res.orderType)
          ? res
          : res.subCarts.find(
              ({ subCartId, proxySubCart }) =>
                (isRegularOrder(res.orderType) && subCartId === res.id) ||
                (isProxyOrder(res.orderType) && proxySubCart),
            );
        dispatch(updateIsLoading(false));
        setOrderType(res?.orderType as OrderType);
        setCount(Number(currentCart?.totalEntries));
        dispatch(updateCart(res));
        StorageService.getLocalStorage().remove?.(CartConstantsEnum.AnonymousUser);
        if (
          // if there are products in cart then only call merge cart
          (StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartObj)?.length ?? 0) != 0
        ) {
          createToast(
            {
              theme: CartNotificationTheme.PURPLE,
              rightSeperator: false,
              content: mergeCartSuccessText,
              clickIcon: 'times',
              timeOut: TOAST_TIMEOUT,
            },
            deleteToast,
          );
        }
        //Setting a flag in session storage to determine if merge cart has been called previously
        if (isActor() && !StorageService.getSessionStorage().retrieve(CartConstantsEnum.MergeCartCompleted)) {
          StorageService.getSessionStorage().store(CartConstantsEnum.MergeCartCompleted, true);
        }
      })
      .catch(() => {
        dispatch(updateIsLoading(false));

        // If merge cart flow fails then we are clearing anon user cart details and AnonymousUser flag.
        StorageService.getLocalStorage().remove(CartConstantsEnum.CartId);
        StorageService.getLocalStorage().remove(CartConstantsEnum.CartReqVersion);
        StorageService.getLocalStorage().remove(CartConstantsEnum.AnonymousUser);
        //removing flag from sessionStorage if api fails
        StorageService.getSessionStorage().remove(CartConstantsEnum.MergeCartCompleted);
      })
      .finally(() => {
        StorageService.getLocalStorage().remove(CartConstantsEnum.CartObj);
      });
  };

  const createWishList = () => {
    myListApi
      .createList({
        name: ListType.Wishlist,
        listType: ListType.Wishlist,
        entries: [],
        description: ListType.Wishlist,
        category: ListType.Wishlist,
      })
      .then((newList: ListResponse) => {
        dispatch(updateMyList(newList ?? {}));
        StorageService.getLocalStorage().store?.(CartConstantsEnum.ListId, newList.listId as string);
      })
      .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps));
  };

  const getWishlist = () => {
    dispatch(
      getMyListsByTypeSliceReq({
        type: ListType.Wishlist,
        projections: ApiProjections.DETAILED,
      }) as unknown as AnyAction,
    ).then((listRes: PayloadAction<UserListResponse>) => {
      if (!listRes) {
        return;
      }
      if (listRes?.payload?.lists?.length === 0) {
        createWishList();
      } else {
        dispatch(updateMyList(listRes?.payload?.lists?.[0] ?? {}));
        StorageService.getLocalStorage().store?.(
          CartConstantsEnum.ListId,
          listRes?.payload?.lists?.[0].listId as string,
        );
      }
    });
  };

  const getIcon = (isGroupCart = false) => {
    switch (true) {
      case isProxyOrder(orderType):
        return (
          <NextgenImage
            loaderType={LoaderType.WEBFONTS}
            src="/icons/proxy-cart.svg"
            alt={'proxy-icon'}
            isRelativeURL={true}
            aria-label="proxy shopping cart"
          />
        );
      case isGroupCart:
        return (
          <NextgenImage
            loaderType={LoaderType.WEBFONTS}
            src="/icons/group-cart.svg"
            alt={'gco-icon'}
            isRelativeURL={true}
            aria-label="group order shopping cart"
          />
        );
      default:
        return icon ? icon : <FAIcon theme={Theme.BLACK} iconName="cart-shopping" />;
    }
  };

  const getMinicartCountClass = (isGroupCart = false) => {
    switch (true) {
      case isGroupCart || isProxyOrder(orderType):
        return '-gco';
      default:
        return '';
    }
  };

  const getIconWithCount = (isGroupCart = false) => (
    <div className={styles[`header-icon${isGroupCart || isProxyOrder(orderType) ? '-gco' : ''}`]} key={miniCartKey()}>
      {getIcon(isGroupCart)}
      {count > 0 && (
        <div className={styles[`mini-cart-count${getMinicartCountClass(isGroupCart)}`]}>
          {' '}
          <span className={styles[`mini-cart-count-text${isGroupCart || isProxyOrder(orderType) ? '-gco' : ''}`]}>
            {count <= 99 ? count : '99+'}
          </span>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    // subscribing the events for count update
    const clearCartSub: string[] = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.ClearCartEntries],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: clearCart,
    });
    // subscribing the events for count update
    const fetchCartSub: string[] = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.AddToCart, CartEventsEnum.RemoveCartEntry, CartEventsEnum.UpdateCartEntryQty],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: (cartEvent: string, { cart }: CartEventPayloadType) => {
        updateMiniCartCount(cart as Cart);
      },
    });

    const updateMiniCartIcon: string[] = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.UpdateMiniCartIcon],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: (cartEvent: string, { cart }: CartEventPayloadType) => {
        setCount(cart?.totalEntries as number);
        setOrderType(cart?.orderType as OrderType);
      },
    });

    const fetchWishlist: string[] = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.GetWishlist],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: getWishlist,
    });

    // subscribing event for warehouse Id update
    const updateWarehouse = EventService.subscribe(
      `web.domain.${WebEventsEnum.WarehouseIdUpdated}`,
      WebEventsVersionsEnum['1.0.0'],
      () => {
        const callMergeCartForImpersonation =
          isActor() && !StorageService.getSessionStorage().retrieve(CartConstantsEnum.MergeCartCompleted);

        //Call merge cart in case of ABO and customer user type
        const callMergeCartCondition =
          (!isAnon && StorageService.getLocalStorage().retrieve(CartConstantsEnum.AnonymousUser)) ||
          callMergeCartForImpersonation;
        /**
         * We are calling get cart api for following two conditions
         * 1 - cart route && user is anonymous
         * 2 - cart router && merge cart api condtion is false
         *
         * Condtion to block get cart call when merge cart is already executing
         *
         */
        if (router.pathname?.includes(RouteType.CART) && (isAnon || !callMergeCartCondition)) {
          getCart();
        }
      },
    );

    subscriptionIds.current = [
      ...clearCartSub,
      ...fetchCartSub,
      ...updateMiniCartIcon,
      ...fetchWishlist,
      ...updateWarehouse,
    ];

    return () => {
      // unsub all events on reload or rerender
      CartEvents.unsubscribe(subscriptionIds.current);
    };
  }, []);
  const handleStorageChange = (event: StorageEvent) => {
    const { key, oldValue, newValue } = event;

    if (!newValue) {
      return;
    }
    if (key === ProfileConstantsEnum.registrationAttributes && oldValue) {
      const lsNewValue = StorageService.getLocalStorage().retrieve(
        ProfileConstantsEnum.registrationAttributes,
      ) as unknown as registrationAttributesType;

      const decodedOldValue = JSON.parse(decodeURIComponent(window.atob(oldValue || '')));

      if (
        decodedOldValue?.sponsor?.accountNumber !== '' &&
        decodedOldValue?.sponsor?.accountNumber !== lsNewValue?.sponsor?.accountNumber
      ) {
        setShowRegSponsorChangePopup(true);
      }
    }
  };
  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const cartIdInLS = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
    if (!cartIdInLS) {
      setOrderType(isABO() && isGroupOrder(orderTypeConfig) ? OrderType.Gco : OrderType.Regular);
    }
    //Set anonymous user flag true in local storage
    if (isAnon && !StorageService.getLocalStorage().retrieve(CartConstantsEnum.AnonymousUser)) {
      StorageService.getLocalStorage().store(CartConstantsEnum.AnonymousUser, true);
    }

    const callMergeCartForImpersonation =
      isActor() && !StorageService.getSessionStorage().retrieve(CartConstantsEnum.MergeCartCompleted);

    //Call merge cart in case of ABO and customer user type
    const callMergeCartCondition =
      (!isAnon && StorageService.getLocalStorage().retrieve(CartConstantsEnum.AnonymousUser)) ||
      callMergeCartForImpersonation;
    if (callMergeCartCondition) {
      mergeCart();
    } else {
      getCart();
    }

    // Fetch User Lists If not anonymous
    if (!isAnon && fireListApi(router.pathname as string) && !router.pathname?.includes(RouteType.LIST)) {
      myListApi
        .getAllListByUser({
          projections: ApiProjections.FULL,
        })
        .then((res: UserListResponse) => {
          const wishList = res.lists?.find(({ listType }) => listType === ListType.Wishlist);

          if (!wishList) {
            createWishList();
          }
          //Do not update listByUser in case of registered customer
          if (!isRegistered()) {
            dispatch(updateListsByUser(res));
          }
          if (wishList) {
            dispatch(updateMyList(wishList as ListResponse));
          }
        })
        .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps));
    }
  }, [isAnon, router.params?.locale]);

  const miniCartKey = () => (count <= 99 ? String(count) : '99+');
  return (
    <>
      {showRegSponsorChangePopup && (
        <Modal
          primaryButtonLabel={t('cart.miniCart.multipleRegWarningSuccess')}
          onPrimaryButtonClick={() => {
            setShowRegSponsorChangePopup(false);
            getCart();
          }}
          headline={t('cart.miniCart.multipleRegWarning')}
        />
      )}
      <div className={`${styles['miniCart-component']} ${elementCustomrClass}`} data-testid="mini-cart-test-id">
        {getIconWithCount(isABO() && orderType === OrderType.Gco)}
      </div>
    </>
  );
};

export type { MiniCartProps };
